import React, { useMemo, useState } from 'react';
import {
  Button,
  ComboBox,
  Tag,
  InlineNotification,
} from '@carbon/react';
import { CameraAction, TrashCan } from '@carbon/react/icons';

import { useGetElementsQuery } from '@/redux/new_services/elements';
import {
  useLinkPairMutation,
  useUnlinkPairMutation,
} from '@/redux/new_services/pair';
import MediaPreview, { previewSrc } from './MediaPreview';

const ORIENTATION_LABEL = {
  0: 'Querformat',
  1: 'Hochkant',
  2: 'Multi',
};

// ElementPairAssignment — Tab content for the element edit form. Lets the
// admin connect this element to its Hoch+Quer counterpart so the gallery
// renders them as one tile and the player can swap them at runtime.
//
// Pair partners must have the *opposite* orientation. Multi-format elements
// (orientation = 2) cannot be paired — they already cover both formats on
// their own.
const ElementPairAssignment = ({ item, editable = true, onPairChanged = () => {} }) => {
  const [linkPair, { isLoading: isLinking }] = useLinkPairMutation();
  const [unlinkPair, { isLoading: isUnlinking }] = useUnlinkPairMutation();
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);

  const myOrientation = item?.orientation;
  const oppositeFilter = myOrientation === 0
    ? 'orientation_portrait'
    : myOrientation === 1
      ? 'orientation_landscape'
      : null;

  // Only fetch candidates when we actually need a partner picker — saves a
  // request when the element is already paired or is multi-format.
  const skipFetch = !editable || !oppositeFilter || Boolean(item?.pair_id);
  const { data } = useGetElementsQuery(
    {
      skip: 0,
      limit: 100,
      filter: {
        pairing: ['pairing_unpaired'],
        orientation: [oppositeFilter],
      },
    },
    { skip: skipFetch },
  );
  const candidates = useMemo(
    () => (data?.data || []).filter((m) => m.model.ID !== item?.model?.ID),
    [data, item],
  );

  const handleLink = async () => {
    if (!selected || !item?.model?.ID) return;
    try {
      await linkPair({ id: item.model.ID, partnerId: selected.model.ID }).unwrap();
      setSelected(null);
      setError(null);
      onPairChanged();
    } catch (e) {
      setError('Paar konnte nicht verknüpft werden');
    }
  };

  const handleUnlink = async () => {
    if (!item?.model?.ID) return;
    try {
      await unlinkPair(item.model.ID).unwrap();
      setError(null);
      onPairChanged();
    } catch (e) {
      setError('Paar konnte nicht gelöst werden');
    }
  };

  if (myOrientation === 2) {
    return (
      <InlineNotification
        kind="info"
        title="Hinweis"
        subtitle="Multi-Format-Elemente decken Hoch- und Querformat schon ab und brauchen kein Pair."
        hideCloseButton
        lowContrast
      />
    );
  }

  const partner = item?._pair_partner || null;

  return (
    <div className="element-pair-assignment">
      <p className="element-pair-assignment__lead">
        Ein Hoch+Quer-Paar zeigt denselben Inhalt in zwei Formaten. Der Player
        wählt zur Laufzeit anhand der Geräteorientierung das passende Bild.
        In der Element-Übersicht erscheint das Paar als eine einzige Tile.
      </p>

      {error && (
        <InlineNotification
          kind="error"
          title="Fehler"
          subtitle={error}
          onCloseButtonClick={() => setError(null)}
          lowContrast
        />
      )}

      {item?.pair_id ? (
        <section className="element-pair-assignment__current">
          <header>
            <CameraAction size={20} />
            <h5>Aktueller Partner</h5>
          </header>
          {partner ? (
            <div className="element-pair-assignment__partner">
              <div className="element-pair-assignment__partner-thumb">
                {previewSrc(partner) ? (
                  <MediaPreview media={partner} fit="cover" />
                ) : (
                  <CameraAction size={24} />
                )}
              </div>
              <div className="element-pair-assignment__partner-info">
                <strong>{partner.name}</strong>
                <Tag type="cool-gray">{ORIENTATION_LABEL[partner.orientation] || '?'}</Tag>
              </div>
            </div>
          ) : (
            <p className="element-pair-assignment__partner-missing">
              Partner ist verlinkt (pair_id: <code>{item.pair_id}</code>),
              aber das andere Element wurde nicht mit geladen — vermutlich auf
              einer anderen Seite oder gefiltert. Aktualisiere die Übersicht,
              wenn du es brauchst.
            </p>
          )}
          <Button
            kind="ghost"
            size="sm"
            renderIcon={TrashCan}
            onClick={handleUnlink}
            disabled={!editable || isUnlinking}
          >
            Pair lösen
          </Button>
        </section>
      ) : (
        <section className="element-pair-assignment__picker">
          <ComboBox
            id="pair-partner-combo"
            titleText="Partner auswählen"
            helperText={
              myOrientation === 0
                ? 'Wähle ein ungepaartes Hochformat-Element als Partner'
                : 'Wähle ein ungepaartes Querformat-Element als Partner'
            }
            items={candidates}
            itemToString={(c) => (c ? c.name : '')}
            selectedItem={selected}
            onChange={(e) => setSelected(e?.selectedItem || null)}
            disabled={!editable}
          />
          <Button
            onClick={handleLink}
            disabled={!editable || !selected || isLinking}
            renderIcon={CameraAction}
          >
            Als Hoch+Quer-Paar verknüpfen
          </Button>
        </section>
      )}
    </div>
  );
};

export default ElementPairAssignment;
